import { constructionCost, levelBuildTimeMs, maxLevel, productionLines, upgradeRule } from './schema.js';
import type { Economy } from './Economy.js';
import type {
    GameObject,
    InitialPlacementState,
    OutputState,
    Placement,
    PlacementState,
    ResourceAmount,
} from '../types.js';

function perOutput<T>(
    authored: T | Record<string, T> | undefined,
    resource: string,
    single: boolean,
): T | undefined {
    if (authored == null) return undefined;
    if (typeof authored === 'object' && !Array.isArray(authored)) {
        return (authored as Record<string, T>)[resource];
    }
    return single ? (authored as T) : undefined;
}

export function initState(
    object: GameObject,
    state: InitialPlacementState = {},
): PlacementState {
    const requestedLevel = Number.isFinite(state.level) ? Math.trunc(state.level!) : 1;
    const level = Math.min(Math.max(requestedLevel, 1), maxLevel(object));
    const lines = productionLines(object, level);
    const single = lines.length === 1;

    return {
        level,
        constructionMs: state.constructionMs ?? null,
        constructionTotalMs: state.constructionTotalMs ?? null,
        outputs: lines.map((line) => ({
            resource: line.resource,
            stored: perOutput(state.stored, line.resource, single) ?? 0,
            capacity: perOutput(state.capacity, line.resource, single) ?? line.capacity ?? null,
            remainingMs:
                perOutput(state.remainingMs, line.resource, single) ?? line.timeMs,
        })),
    };
}

function reState(object: GameObject, state: PlacementState, level: number): void {
    const kept = new Map(state.outputs.map((o) => [o.resource, o]));
    state.level = level;
    state.outputs = productionLines(object, level).map((line) => {
        const previous = kept.get(line.resource);
        return {
            resource: line.resource,
            stored: previous?.stored ?? 0,
            capacity: line.capacity !== undefined ? line.capacity : previous?.capacity ?? null,
            remainingMs: line.timeMs,
        };
    });
}

export function beginConstruction(object: GameObject, state: PlacementState): void {
    const time = levelBuildTimeMs(object, state.level);
    state.constructionMs = time > 0 ? time : null;
    state.constructionTotalMs = state.constructionMs;
}

export function underConstruction(state: PlacementState): boolean {
    return (state.constructionMs ?? 0) > 0;
}

export function constructionProgress(
    object: GameObject,
    state: PlacementState,
): number | null {
    if (!underConstruction(state)) return null;
    const total = state.constructionTotalMs ?? levelBuildTimeMs(object, state.level);
    if (!total) return null;
    return 1 - Math.min(Math.max(state.constructionMs ?? 0, 0), total) / total;
}

function outputFull(output: OutputState): boolean {
    return output.capacity != null && output.stored >= output.capacity;
}

export function isFull(state: PlacementState): boolean {
    return state.outputs.length > 0 && state.outputs.every(outputFull);
}

export function cycleProgressByOutput(
    object: GameObject,
    state: PlacementState,
): number[] {
    const lines = productionLines(object, state.level);
    return state.outputs.flatMap((output, index) => {
        const timeMs = lines[index]?.timeMs;
        if (!timeMs) return [];
        const remaining = Math.min(Math.max(output.remainingMs ?? 0, 0), timeMs);
        return [1 - remaining / timeMs];
    });
}

export function cycleProgress(object: GameObject, state: PlacementState): number | null {
    const all = cycleProgressByOutput(object, state);
    return all.length ? Math.max(...all) : null;
}

function inputCosts(line: ResourceAmount[]): ResourceAmount[] {
    const totals = new Map<string, number>();
    line.forEach(({ resource, quantity }) => {
        const amount = quantity ?? 0;
        if (amount <= 0) return;
        totals.set(resource, (totals.get(resource) ?? 0) + amount);
    });
    return [...totals].map(([resource, quantity]) => ({ resource, quantity }));
}

export function hydrateState(object: GameObject, saved: PlacementState): PlacementState {
    const state = initState(object, {
        level: saved.level,
        constructionMs: saved.constructionMs,
        constructionTotalMs: saved.constructionTotalMs,
    });
    const savedOutputs = new Map(saved.outputs.map((output) => [output.resource, output]));

    state.outputs = state.outputs.map((output) => {
        const previous = savedOutputs.get(output.resource);
        if (!previous) return output;

        return {
            resource: output.resource,
            stored: Math.max(0, previous.stored),
            capacity: previous.capacity ?? output.capacity,
            remainingMs: previous.remainingMs ?? output.remainingMs,
        };
    });
    return state;
}

function advanceOutput(
    output: OutputState,
    line: ReturnType<typeof productionLines>[number],
    elapsedMs: number,
    economy: Economy,
): boolean {
    if (!line?.timeMs || (line.quantity ?? 0) <= 0 || elapsedMs <= 0) return false;
    if (outputFull(output)) return false;

    const beforeStored = output.stored;
    const beforeRemaining = output.remainingMs;
    const timeToCycle = output.remainingMs ?? line.timeMs;
    if (elapsedMs < timeToCycle) {
        output.remainingMs = timeToCycle - elapsedMs;
        return output.remainingMs !== beforeRemaining;
    }

    const afterFirstCycle = elapsedMs - timeToCycle;
    const possibleCycles = 1 + Math.floor(afterFirstCycle / line.timeMs);
    const room = output.capacity == null ? Infinity : output.capacity - output.stored;
    const capacityCycles = Number.isFinite(room)
        ? Math.max(0, Math.floor(room / line.quantity))
        : possibleCycles;
    const costs = inputCosts(line.inputs ?? []);
    const inputCycles = costs.length
        ? Math.min(...costs.map(({ resource, quantity }) => Math.floor(economy.get(resource) / quantity)))
        : possibleCycles;
    const completedCycles = Math.max(0, Math.min(possibleCycles, capacityCycles, inputCycles));

    if (completedCycles > 0) {
        const totalCosts = costs.map(({ resource, quantity }) => ({
            resource,
            quantity: quantity * completedCycles,
        }));
        if (totalCosts.length && !economy.spend(totalCosts)) {
            output.remainingMs = 0;
            return output.remainingMs !== beforeRemaining;
        }

        output.stored += line.quantity * completedCycles;
    }

    if (completedCycles < possibleCycles) {
        // The next cycle was reached but could not be stored or paid for.
        output.remainingMs = 0;
    } else {
        const timeAfterCycles = afterFirstCycle - (completedCycles - 1) * line.timeMs;
        output.remainingMs = line.timeMs - timeAfterCycles;
    }

    return output.stored !== beforeStored || output.remainingMs !== beforeRemaining;
}

/**
 * Advance construction and every production line.
 *
 * Construction always consumes real elapsed time, while `productionScale`
 * (happiness) only stretches the production lines. Scaling the build timer
 * too would make an authored `buildTimeMs` mean something different per
 * game state, and at zero happiness the site would never finish at all.
 */
export function advance(
    placement: Placement,
    economy: Economy,
    elapsedMs: number,
    productionScale = 1,
): boolean {
    const { object, state } = placement;
    if (elapsedMs <= 0) return false;

    let remaining = elapsedMs;
    let changed = false;

    if (underConstruction(state)) {
        const left = state.constructionMs ?? 0;
        if (remaining < left) {
            state.constructionMs = left - remaining;
            return true;
        }
        state.constructionMs = null;
        state.constructionTotalMs = null;
        remaining -= left;
        changed = true;
    }

    const lines = productionLines(object, state.level);
    const producing = remaining * productionScale;
    if (!lines.length || producing <= 0) return changed;

    state.outputs.forEach((output, index) => {
        const line = lines[index];
        if (line && advanceOutput(output, line, producing, economy)) changed = true;
    });

    return changed;
}

export function tick(
    placement: Placement,
    economy: Economy,
    deltaMs: number,
    productionScale = 1,
): boolean {
    return advance(placement, economy, deltaMs, productionScale);
}

export function catchUp(
    placement: Placement,
    economy: Economy,
    elapsedMs: number,
    productionScale = 1,
): boolean {
    return advance(placement, economy, elapsedMs, productionScale);
}

export function canCollect(placement: Placement): boolean {
    return placement.state.outputs.some((output) => output.stored > 0);
}

export function collect(placement: Placement, economy: Economy): boolean {
    let collected = false;

    placement.state.outputs.forEach((output) => {
        if (!output.stored) return;
        economy.add(output.resource, output.stored);
        output.stored = 0;
        collected = true;
    });

    return collected;
}

export function nextLevelCost(
    object: GameObject,
    state: PlacementState,
): ResourceAmount[] | null {
    if (state.level >= maxLevel(object)) return null;
    return constructionCost(object, state.level + 1);
}

export function canUpgrade(placement: Placement, economy: Economy): boolean {
    if (underConstruction(placement.state)) return false;
    const cost = nextLevelCost(placement.object, placement.state);
    return cost !== null && economy.canAfford(cost);
}

export function upgrade(placement: Placement, economy: Economy): boolean {
    if (underConstruction(placement.state)) return false;
    const cost = nextLevelCost(placement.object, placement.state);
    if (cost === null || !economy.spend(cost)) return false;

    const wait = upgradeRule(placement.object, placement.state.level)?.timeMs ?? 0;
    reState(placement.object, placement.state, placement.state.level + 1);

    placement.state.constructionMs = wait > 0 ? wait : null;
    placement.state.constructionTotalMs = placement.state.constructionMs;
    return true;
}
